import {
  EVENT_PUSH_RECEIVED,
  PUSH_CHECK_PERMISSIONS,
  PUSH_CLEAR_CUSTOMER,
  PUSH_GET_CUSTOMER,
  PUSH_GET_ID,
  PUSH_GET_TOKEN,
  PUSH_REQUEST_PERMISSIONS,
  PUSH_SET_CUSTOMER_EMAIL,
  PUSH_SET_CUSTOMER_ID,
  PUSH_SIMULATE_PUSH_RECEIVED,
  type PermissionState,
  type PushReceivedPayload,
} from '@/pwa/bridge-v2/protocol';
import type { Bridge } from '../bridge';

// =============================================================================
// bridge.push — first-class push notifications plugin surface
// =============================================================================
// Native: backed by Capacitor.PushNotifications + the AppEase shell's
// registration flow. PWA: backed by Web Push + register-push-token. Both
// runtimes return the same shapes.
//
// Primary identifier is the Push ID (`push_tokens.id` UUID), surfaced via
// `getId()`. Use it for targeting via the platform's push send API and as
// the canonical id for this device's push registration. The raw OS token is
// still reachable via `getToken()` for debugging.
//
// Customer enrichment (`setCustomerId`, `setCustomerEmail`) attaches
// wrapper-owned identifiers to the registration. The values are stored
// locally in the wrapped page immediately and flushed to the platform as
// soon as push is registered — so it's safe to call these BEFORE prompting
// for push permission.

export interface PushCustomerState {
  customerId: string | null;
  email: string | null;
  /** True when local values are queued waiting for push registration. */
  pending: boolean;
}

export interface PushSurface {
  checkPermissions(): Promise<{ permission: PermissionState }>;
  requestPermissions(): Promise<{ permission: PermissionState; id?: string }>;
  /** Primary: returns the platform-issued Push ID (push_tokens.id). Null
   *  until registration completes. Use this for targeting. */
  getId(): Promise<{ id: string | null }>;
  /** Debug-only: returns the raw OS push token (native) or the same Push ID
   *  (PWA). Most wrappers should use `getId()` instead. */
  getToken(): Promise<{ token: string | null }>;
  /** Attach a wrapper-owned customer id to this device's push registration.
   *  Safe to call before push is registered — the value is stored locally
   *  and flushed automatically when registration lands. */
  setCustomerId(customerId: string | null): Promise<{ applied: boolean; pending: boolean }>;
  /** Attach a wrapper-owned customer email to this device's push registration. */
  setCustomerEmail(email: string | null): Promise<{ applied: boolean; pending: boolean }>;
  /** Read current local enrichment state (synchronous on host; resolves
   *  through the bridge). */
  getCustomer(): Promise<PushCustomerState>;
  /** Clear both customer id and email locally and on the platform. */
  clearCustomer(): Promise<{ applied: boolean; pending: boolean }>;
  /** Round-trip test: the host returns immediately and then emits a canned
   *  `push.received` event back to the page. Use to verify a push listener
   *  is wired without sending a real notification. */
  simulatePushReceived(): Promise<{ delivered: boolean; notification: unknown }>;
  /** Subscribe to inbound push notifications. Returns an unsubscribe
   *  function. Native: forwarded from Capacitor.PushNotifications. PWA:
   *  forwarded from the service worker via usePWAPushMessageListener. */
  onReceived(callback: (notification: PushReceivedPayload) => void): () => void;
}

export const buildPush = (b: Bridge): PushSurface => ({
  checkPermissions: () =>
    b.request<{ permission: PermissionState }>(PUSH_CHECK_PERMISSIONS),
  requestPermissions: () =>
    b.request<{ permission: PermissionState; id?: string }>(PUSH_REQUEST_PERMISSIONS),
  getId: () =>
    b.request<{ id: string | null }>(PUSH_GET_ID),
  getToken: () =>
    b.request<{ token: string | null }>(PUSH_GET_TOKEN),
  setCustomerId: (customerId) =>
    b.request<{ applied: boolean; pending: boolean }>(PUSH_SET_CUSTOMER_ID, { customerId }),
  setCustomerEmail: (email) =>
    b.request<{ applied: boolean; pending: boolean }>(PUSH_SET_CUSTOMER_EMAIL, { email }),
  getCustomer: () =>
    b.request<PushCustomerState>(PUSH_GET_CUSTOMER),
  clearCustomer: () =>
    b.request<{ applied: boolean; pending: boolean }>(PUSH_CLEAR_CUSTOMER),
  simulatePushReceived: () =>
    b.request<{ delivered: boolean; notification: unknown }>(PUSH_SIMULATE_PUSH_RECEIVED),
  onReceived: (callback) => b.on<PushReceivedPayload>(EVENT_PUSH_RECEIVED, callback),
});
